import { useState } from 'react';
import { Button, Group, Modal, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { DataObject } from '@/api/objects';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

interface ListingFile {
  path: string;
  size_bytes: number;
  crc32c: string | null;
}
interface ListingSnapshot {
  files: ListingFile[];
  digest: string | null;
  file_count: number;
  total_bytes: number;
}

/** A button that opens the object's listing snapshot in a modal (staff object page). */
export function ObjectListingModal({ object }: { object: DataObject }) {
  const { t } = useTranslation('projects');
  const { bytes } = useFormat();
  const [opened, setOpened] = useState(false);
  const snap = object.listing_snapshot as ListingSnapshot | null | undefined;
  const files = snap?.files ?? [];

  return (
    <>
      <Group>
        <Button variant="default" onClick={() => setOpened(true)} disabled={files.length === 0}>
          {files.length > 0 ? t('staff.showListing', { count: files.length }) : t('staff.noListing')}
        </Button>
      </Group>
      <Modal opened={opened} onClose={() => setOpened(false)} title={t('staff.listing')} size="xl">
        <Text ff="monospace" fz={11} c={brand.textMuted} mb="sm">
          {snap?.digest ?? '—'}
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>path</Table.Th>
              <Table.Th>size</Table.Th>
              <Table.Th>crc32c</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {files.map((f) => (
              <Table.Tr key={f.path}>
                <Table.Td>
                  <Text ff="monospace" fz={12}>
                    {f.path}
                  </Text>
                </Table.Td>
                <Table.Td>{bytes(f.size_bytes)}</Table.Td>
                <Table.Td>
                  <Text ff="monospace" fz={12}>
                    {f.crc32c ?? '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Modal>
    </>
  );
}
